import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { reportAPI, clientGroupAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BarChart3, FileText, TrendingUp, Calendar, Download, Printer,
  ChevronLeft, ChevronRight, PieChart as PieChartIcon, Activity, Filter
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { formatDateLong } from '../lib/dateFormat';

const CHART_COLORS = [
  'hsl(174, 74%, 34%)', 'hsl(199, 84%, 48%)', 'hsl(40, 92%, 56%)',
  'hsl(142, 72%, 44%)', 'hsl(0, 72%, 52%)', '#E1306C', '#0A66C2'
];

const STATUS_COLORS = {
  'Published': 'hsl(174, 74%, 34%)',
  'Scheduled': 'hsl(199, 84%, 48%)',
  'Approved': 'hsl(142, 72%, 44%)',
  'In Review': 'hsl(40, 92%, 56%)',
  'Draft': 'hsl(215, 20%, 55%)',
  'Rejected': 'hsl(0, 72%, 52%)',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ALL_PLATFORMS = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube', 'pinterest', 'gmb', 'substack'];

export default function ReportsPage() {
  const { currentWorkspace } = useWorkspace();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Load groups
  useEffect(() => {
    if (currentWorkspace?.id) {
      clientGroupAPI.list(currentWorkspace.id)
        .then(res => setGroups(sortClientsAlpha(res.data || [])))
        .catch(() => {});
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      setLoading(true);
      reportAPI.monthly(currentWorkspace.id, year, month, selectedPlatforms, selectedGroupId)
        .then(res => setReport(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id, year, month, selectedPlatforms, selectedGroupId]);

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handlePrint = () => window.print();

  const summary = report?.summary || {};
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover/90 backdrop-blur border rounded-lg p-3 text-xs shadow-lg">
          <p className="font-medium mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color || p.fill }}>
              {p.name}: {p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-secondary rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-secondary rounded-xl" />)}
        </div>
        <div className="h-64 bg-secondary rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in-up print:p-2" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-display font-semibold">Monthly Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Performance summary for <span className="text-gold font-medium">{currentWorkspace?.name}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-button">
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      {/* Print Header (visible only when printing) */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold">{currentWorkspace?.name} — Monthly Report</h1>
        <p className="text-sm text-gray-600">{MONTHS[month - 1]} {year}</p>
      </div>

      {/* Month Selector */}
      <Card className="bg-card/60 backdrop-blur print:shadow-none print:border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={prevMonth} className="print:hidden">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-lg font-display font-semibold min-w-[200px] text-center" data-testid="report-month-title">
                {MONTHS[month - 1]} {year}
              </h2>
              <Button variant="ghost" size="icon" onClick={nextMonth} className="print:hidden">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="print:hidden"
                data-testid="toggle-filters-button"
              >
                <Filter className="h-4 w-4 mr-2" /> Filters
                {(selectedPlatforms.length > 0 || selectedGroupId) && (
                  <Badge className="ml-1.5 bg-primary/20 text-primary text-[10px] px-1.5">
                    {selectedPlatforms.length + (selectedGroupId ? 1 : 0)}
                  </Badge>
                )}
              </Button>
              <Badge className="bg-secondary text-secondary-foreground" data-testid="report-total-posts">
                {summary.total_posts || 0} Total Posts
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="bg-card/60 backdrop-blur print:hidden animate-fade-in-up" data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Platform Filters */}
              <div className="flex-1">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 block">
                  Filter by Platform
                </Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_PLATFORMS.map(platform => (
                    <label
                      key={platform}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs capitalize ${
                        selectedPlatforms.includes(platform)
                          ? 'bg-primary/10 border-primary/30 text-foreground'
                          : 'bg-secondary/30 border-border text-muted-foreground hover:bg-secondary/50'
                      }`}
                      data-testid={`filter-platform-${platform}`}
                    >
                      <Checkbox
                        checked={selectedPlatforms.includes(platform)}
                        onCheckedChange={() => togglePlatform(platform)}
                        className="h-3.5 w-3.5"
                      />
                      {platform}
                    </label>
                  ))}
                </div>
                {selectedPlatforms.length > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-xs mt-1 h-auto p-0"
                    onClick={() => setSelectedPlatforms([])}
                    data-testid="clear-platform-filters"
                  >
                    Clear platforms
                  </Button>
                )}
              </div>

              {/* Group Filter */}
              {groups.length > 0 && (
                <div className="w-full md:w-56">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 block">
                    Filter by Group
                  </Label>
                  <Select value={selectedGroupId} onValueChange={v => setSelectedGroupId(v === 'all' ? '' : v)}>
                    <SelectTrigger data-testid="filter-group-select">
                      <SelectValue placeholder="All Groups" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Groups</SelectItem>
                      {groups.map(g => (
                        <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Total', value: summary.total_posts || 0, color: 'text-foreground' },
          { label: 'Published', value: summary.published || 0, color: 'text-emerald-400' },
          { label: 'Scheduled', value: summary.scheduled || 0, color: 'text-cyan-400' },
          { label: 'Approved', value: summary.approved || 0, color: 'text-green-400' },
          { label: 'In Review', value: summary.in_review || 0, color: 'text-amber-400' },
          { label: 'Draft', value: summary.draft || 0, color: 'text-muted-foreground' },
          { label: 'Rejected', value: summary.rejected || 0, color: 'text-rose-400' },
        ].map((kpi, i) => (
          <Card key={i} className="rounded-xl bg-card/60 backdrop-blur print:border">
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-display font-semibold ${kpi.color}`} data-font="display">{kpi.value}</p>
              <p className="text-xs text-muted-foreground">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown Pie */}
        <Card className="bg-card/60 backdrop-blur print:border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PieChartIcon className="h-4 w-4 text-gold" /> Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(report?.status_breakdown || []).length === 0 ? (
              <div className="flex items-center justify-center h-[250px]">
                <p className="text-sm text-muted-foreground">No posts this month.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={report.status_breakdown}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={55}
                    paddingAngle={2}
                  >
                    {(report.status_breakdown || []).map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.name] || CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Platform Breakdown */}
        <Card className="bg-card/60 backdrop-blur print:border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-gold" /> Platform Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(report?.platform_breakdown || []).length === 0 ? (
              <div className="flex items-center justify-center h-[250px]">
                <p className="text-sm text-muted-foreground">No platform data this month.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={report.platform_breakdown}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.35} />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Posts" radius={[4, 4, 0, 0]}>
                    {(report.platform_breakdown || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Activity */}
      <Card className="bg-card/60 backdrop-blur print:border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-gold" /> Daily Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(report?.daily_activity || []).length === 0 ? (
            <div className="flex items-center justify-center h-[200px]">
              <p className="text-sm text-muted-foreground">No activity data for this month.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={report.daily_activity}>
                <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.35} />
                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} tickFormatter={v => v.slice(8)} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="posts" fill={CHART_COLORS[0]} name="Posts" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Top Posts */}
      <Card className="bg-card/60 backdrop-blur print:border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-gold" /> Top Posts This Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(report?.top_posts || []).length === 0 ? (
            <div className="text-center py-6">
              <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No posts this month.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(report.top_posts || []).map((post, i) => (
                <div key={post.id || i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{post.title || 'Untitled'}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {(post.platforms || []).map(p => (
                        <span key={p} className="text-xs text-muted-foreground capitalize">{p}</span>
                      ))}
                      {post.scheduled_at && (
                        <span className="text-xs text-muted-foreground">
                          {formatDateLong(post.scheduled_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge className={`text-xs ${
                    post.status === 'published' ? 'bg-emerald-500/15 text-emerald-400' :
                    post.status === 'scheduled' ? 'bg-cyan-500/15 text-cyan-400' :
                    'bg-secondary text-secondary-foreground'
                  }`}>
                    {(post.status || 'draft').replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All-Time Totals */}
      <Card className="bg-card/60 backdrop-blur print:border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Workspace Overview (All Time)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'All-Time Posts', value: report?.totals?.all_time_posts || 0 },
              { label: 'Media Assets', value: report?.totals?.total_media || 0 },
              { label: 'Ad Campaigns', value: report?.totals?.total_campaigns || 0 },
              { label: 'Team Members', value: report?.totals?.team_size || 0 },
            ].map((item, i) => (
              <div key={i} className="text-center p-3 rounded-lg bg-secondary/30">
                <p className="text-xl font-display font-semibold" data-font="display">{item.value}</p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
